import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SortingRule } from "react-table";
import styled from "styled-components/macro";
import useSWR from "swr";
import { AccordionCard } from "../../../../components/AccordionCard/AccordionCard";
import { useAuthContext } from "../../../../components/Auth";
import {
  CalendarIcon,
  CartOutlineIcon,
  LocationIcon,
  QuoteIcon,
} from "../../../../components/Icons/Icons";
import { AddressBlock } from "../../../../components/LocationsList/LocationsList";
import { Pagination } from "../../../../components/Pagination/Pagination";
import { Table } from "../../../../components/Table/Table";
import { H5, SoftHeader } from "../../../../components/Typography/Typography";
import { endpoints } from "../../../../endpoints";
import { ContentWrapper } from "../../../../layout/publicPageLayout";
import { WideDetails } from "../../../../layout/shared/DetailPageLayout/DetailPageLayout";
import type {
  IPriceTier,
  ProductSKU,
  WithPagination,
} from "../../../../types/types";
import type {
  PIMProduct,
  PricingPriceTierSchema,
  ProductListPriceSchema,
} from "../../../../types/types.PIM";
import {
  defaultHandleSort,
  formatDate,
  rowHover,
  TablePlaceholder,
  useStoreState,
} from "../../../../util/util";

const PriceTiersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ProductSkuContainer = styled.div`
  padding: 24px;
  border: ${({ theme }) => `1px solid ${theme.secondaryBorder}`};
  border-radius: 8px;
  .accordion-content-wrapper {
    border-bottom: none;
  }
`;

type TableData = ProductListPriceSchema & { sku_name: string };

export const ProductPricingPortfolioTab = ({
  product,
  can_view_list_prices,
}: {
  product: PIMProduct;
  can_view_list_prices: boolean;
}) => {
  const per_page = 20;
  const [list_prices_table_data, set_list_prices_table_data] = useState<
    TableData[]
  >([]);
  const [offset, set_offset] = useState(0);
  const [price_tiers_offset, set_price_tiers_offset] = useState(0);
  const [sorting_rules, set_sorting_rules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({ orderBy: "asc" });
  const [table_pagination, set_table_pagination] = useState({
    perPage: per_page,
    pageCount: 0,
    pageIndex: 0,
  });
  const [price_tiers_pagination, set_price_tiers_pagination] = useState({
    perPage: per_page,
    pageCount: 0,
    pageIndex: 0,
  });
  const { tenant_id, storefront_id } = useStoreState();
  const { roleIsSomeKindOfBuyer, user } = useAuthContext();
  const { t } = useTranslation();
  const { data: product_list_prices, error: product_list_prices_error } =
    useSWR<WithPagination<{ data: ProductListPriceSchema[] }>>([
      endpoints.v2_tenants_tenant_id_products_product_id_list_prices(
        tenant_id,
        product.id
      ),
      useMemo(
        () => ({
          params: {
            offset,
            limit: per_page,
            order_by: sorting_rules.orderBy || "asc",
            ...(sorting_rules.sortBy && { sort_by: sorting_rules.sortBy }),
          },
        }),
        [offset, sorting_rules.orderBy, sorting_rules.sortBy]
      ),
    ]);

  const price_tiers_params = useMemo(() => {
    const params = new URLSearchParams();
    params.append("offset", String(price_tiers_offset));
    params.append("limit", String(per_page));
    params.append("order_by", "asc");
    params.append("buyer_id", user?.tenant_id ?? "");
    return params.toString();
  }, [price_tiers_offset, user?.tenant_id]);

  const { data: price_tiers_pricing, error: price_tiers_pricing_error } =
    useSWR<WithPagination<{ data: PricingPriceTierSchema[] }>>(
      roleIsSomeKindOfBuyer && !!user
        ? `${endpoints.v2_storefronts_storefront_id_pim_products_price_tiers_pricing(
            storefront_id,
            product.id
          )}?${price_tiers_params}`
        : null
    );

  const handle_sort = async (rules: SortingRule<ProductListPriceSchema>[]) => {
    defaultHandleSort(
      rules,
      sorting_rules,
      set_sorting_rules,
      set_list_prices_table_data
    );
  };

  const change_page = (offset: number) => {
    set_offset(offset);
    set_list_prices_table_data([]);
  };

  const change_price_tiers_page = (offset: number) => {
    set_price_tiers_offset(offset);
  };

  const group_price_tiers = (price_tiers: IPriceTier[]) => {
    return price_tiers.reduce<Record<string, IPriceTier[]>>(
      (acc, price_tier) => {
        const key =
          price_tier.destination.id +
          price_tier.delivery_term.id +
          price_tier.payment_term.id +
          price_tier.valid_to_date;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(price_tier);
        return acc;
      },
      {}
    );
  };

  const table_columns = useMemo<ColumnDef<TableData>[]>(
    () => [
      {
        header: t("SKU"),
        accessorKey: "sku_name",
        enableSorting: false,
        cell: (cell) => cell.renderValue(),
      },
      {
        header: t("Min. No. of Units"),
        accessorKey: "minimum_units",
        cell: ({ getValue }) => (
          <span>{Intl.NumberFormat("en-US").format(Number(getValue()))}</span>
        ),
      },
      {
        header: t("Min. Order Quantity"),
        accessorKey: "quantity",
        cell: ({ getValue }) => (
          <span>{Intl.NumberFormat("en-US").format(Number(getValue()))}</span>
        ),
      },
      {
        header: t("Price ($/UoM)"),
        accessorKey: "price",
        cell: ({ getValue }) => (
          <span>
            {Intl.NumberFormat("en-US", {
              style: "decimal",
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            }).format(Number(getValue()))}
          </span>
        ),
      },
      {
        header: t("Valid Until"),
        accessorKey: "valid_until",
        cell: (cell) => cell.renderValue(),
      },
    ],
    [t]
  );

  const get_sku_name = (sku: ProductSKU | null) =>
    sku
      ? `${sku.packaging_type.name} (${sku.package_volume} ${sku.packaging_unit.name})`
      : "";

  useEffect(() => {
    const handle_pricing_data = ({
      data,
      pagination,
    }: WithPagination<{ data: ProductListPriceSchema[] }>) => {
      set_list_prices_table_data(
        data
          .filter(({ sku }) => sku)
          .reduce<TableData[]>(
            (acc, { valid_until, sku, ...rest }) => [
              ...acc,
              {
                ...rest,
                sku,
                valid_until: formatDate(valid_until, true),
                sku_name: get_sku_name(sku),
              },
            ],
            []
          )
      );
      set_table_pagination({
        perPage: per_page,
        pageCount: Math.ceil(pagination.total / per_page),
        pageIndex: pagination.offset / per_page + 1,
      });
    };
    if (product_list_prices) {
      handle_pricing_data(product_list_prices);
    }
  }, [per_page, product_list_prices]);

  useEffect(() => {
    if (price_tiers_pricing) {
      const { pagination } = price_tiers_pricing;
      set_price_tiers_pagination({
        perPage: per_page,
        pageCount: Math.ceil(pagination.total / per_page),
        pageIndex: pagination.offset / per_page + 1,
      });
    }
  }, [price_tiers_pricing]);

  const is_loading_list_prices_data =
    !product_list_prices && !product_list_prices_error;
  const is_loading_price_tiers_data =
    !price_tiers_pricing && !price_tiers_pricing_error;
  const has_price_tiers =
    (price_tiers_pricing?.data?.flatMap(({ price_tiers }) => price_tiers)
      ?.length ?? 0) > 0;

  const product_skus_with_price_tiers = useMemo(() => {
    if (!price_tiers_pricing?.data) return [];
    return price_tiers_pricing.data.filter(
      ({ price_tiers }) => price_tiers.length > 0
    );
  }, [price_tiers_pricing?.data]);
  return (
    <ContentWrapper>
      {!is_loading_price_tiers_data && has_price_tiers && (
        <div className="product-details-section">
          <AccordionCard
            title={t("Price Tiers")}
            expanded={true}
            actionChildren={() => <></>}
          >
            <PriceTiersContainer>
              {product_skus_with_price_tiers.map(
                ({ product_sku, price_tiers }) => (
                  <ProductSkuContainer key={product_sku.id}>
                    <AccordionCard
                      expanded={true}
                      actionChildren={() => <></>}
                      title={() => <H5>{get_sku_name(product_sku)}</H5>}
                      key={product_sku.id}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "18px",
                        }}
                      >
                        {Object.entries(group_price_tiers(price_tiers)).map(
                          ([key, grouped_price_tiers], index) => (
                            <PriceTiersView
                              key={key}
                              price_tiers={grouped_price_tiers}
                              has_border={
                                index <
                                Object.keys(group_price_tiers(price_tiers))
                                  .length -
                                  1
                              }
                            />
                          )
                        )}
                      </div>
                    </AccordionCard>
                  </ProductSkuContainer>
                )
              )}
              <div style={{ alignSelf: "flex-end", width: "fit-content" }}>
                <Pagination
                  pagination={price_tiers_pagination}
                  offset={price_tiers_offset}
                  handlePageClick={change_price_tiers_page}
                  miniPagination={true}
                />
              </div>
            </PriceTiersContainer>
          </AccordionCard>
        </div>
      )}
      {can_view_list_prices && list_prices_table_data.length > 0 ? (
        <div className="product-details-section">
          <AccordionCard
            title={t("List Prices")}
            expanded={true}
            actionChildren={() => <></>}
          >
            <>
              <Table
                columns={table_columns}
                data={list_prices_table_data}
                lastChildleftAlign
                isLoading={is_loading_list_prices_data}
                handleSort={handle_sort}
                error={product_list_prices_error}
                rowHover={rowHover}
                Placeholder={
                  <TablePlaceholder message={t("No items to show.")} />
                }
              />
              <Pagination
                pagination={table_pagination}
                offset={offset}
                handlePageClick={change_page}
                miniPagination={true}
              />
            </>
          </AccordionCard>
        </div>
      ) : (
        <></>
      )}
    </ContentWrapper>
  );
};

const PriceTiersViewContainer = styled.div<{ has_border: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: ${({ theme, has_border }) =>
    has_border ? `1px solid ${theme.secondaryBorder}` : "none"};
  padding-bottom: ${({ has_border }) => (has_border ? "20px" : "0")};
`;

const CustomWideDetails = styled(WideDetails)`
  .infoblock_icon_wrapper {
    margin: 0;
  }
`;

const InfoBlockContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.regular};
`;

const PricingInfoBlock = ({
  icon,
  header,
  content,
}: {
  icon?: JSX.Element;
  header: string;
  content: JSX.Element;
}) => {
  return (
    <InfoBlockContainer>
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        {icon}
        <SoftHeader>{header}</SoftHeader>
      </div>
      <div style={{ marginLeft: "26px" }}>{content}</div>
    </InfoBlockContainer>
  );
};

const PriceTiersView = ({
  price_tiers,
  has_border = false,
}: {
  price_tiers: IPriceTier[];
  has_border?: boolean;
}) => {
  const [table_data, set_table_data] = useState<IPriceTier[]>(price_tiers);
  const { t } = useTranslation();
  const table_columns = useMemo<ColumnDef<IPriceTier>[]>(
    () => [
      {
        header: t("Min. No. of Units"),
        accessorKey: "minimum_sku_quantity",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: t("Min. Order Quantity"),
        accessorKey: "minimum_uom_quantity",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: t("Price ($/UoM)"),
        accessorKey: "price_per_uom",
        cell: (cell) => cell.renderValue(),
      },
    ],
    [t]
  );

  useEffect(() => {
    if (price_tiers.length > 0) {
      set_table_data(
        price_tiers.map((price_tier) => ({
          ...price_tier,
          minimum_sku_quantity: Intl.NumberFormat("en-US").format(
            Number(price_tier.minimum_sku_quantity)
          ),
          minimum_uom_quantity: `${Intl.NumberFormat("en-US").format(
            Number(price_tier.minimum_uom_quantity)
          )} ${price_tier.product_sku.packaging_unit.name}`,
          price_per_uom: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          }).format(Number(price_tier.price_per_uom)),
        }))
      );
    }
  }, [price_tiers]);
  return (
    <PriceTiersViewContainer has_border={has_border}>
      <CustomWideDetails>
        <PricingInfoBlock
          icon={<LocationIcon />}
          header={t("Shipping Address")}
          content={<AddressBlock address={price_tiers[0].destination} />}
        />
        <PricingInfoBlock
          icon={<CalendarIcon />}
          header={t("Valid Until")}
          content={
            <ContentText>
              {formatDate(price_tiers[0].valid_to_date, true)}
            </ContentText>
          }
        />
        <PricingInfoBlock
          icon={<QuoteIcon />}
          header={t("Payment Terms")}
          content={
            <ContentText>{price_tiers[0].payment_term.name}</ContentText>
          }
        />
        <PricingInfoBlock
          icon={<CartOutlineIcon />}
          header={t("Delivery Terms")}
          content={
            <ContentText>{price_tiers[0].delivery_term.name}</ContentText>
          }
        />
      </CustomWideDetails>
      <Table
        columns={table_columns}
        data={table_data}
        lastChildleftAlign
        isLoading={false}
        error={undefined}
        rowHover={rowHover}
        Placeholder={<TablePlaceholder message={t("No items to show.")} />}
      />
    </PriceTiersViewContainer>
  );
};
